import { adminPost } from "@/lib/kbAdminClient";
import { adviseFromChunkQuality, type ChunkQuality } from "./kbAdminAiHelpers";
import type { PanelNote, TrashResult } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  articleSlug: string;
  loadedArticle: any;
  setDeleteWorking: (v: boolean) => void;
  setConfirmDeleteSlug: (v: string | null) => void;
  setConfirmPublishSlug: (v: string | null) => void;
  setConfirmPurgeSlug: (v: string | null) => void;
  setResults: (u: any) => void;
  setLoadedArticle: (u: any) => void;
  setArticleStatus: (v: any) => void;
  setKbMode: (v: any) => void;
  setArticleSlug: (v: string) => void;
  setTrashResults: (u: any) => void;
  setTrashLoading: (v: boolean) => void;
  setPublishWorking: (v: boolean) => void;
  setChunkQuality: (v: ChunkQuality | null) => void;
  setChunkQualityAdvice: (v: string[]) => void;
  refreshDraftCount: () => Promise<void>;
  showNote: ShowNote;
};

export function useKbTrashAndPublishWorkflow(p: Params) {
  async function doSoftDelete(slug: string) {
    p.setDeleteWorking(true);
    p.setConfirmDeleteSlug(null);
    try {
      const response = await adminPost<{ ok: true; slug: string }>("/api/kb/admin/deleteArticle", { slug });
      if (!response.ok) return p.showNote({ tone: "error", title: "Delete failed", text: response.error });
      p.showNote({ tone: "success", title: "Article moved to trash", text: `"${slug}" was soft-deleted. Admins can permanently remove it from the Trash view.` });
      p.setResults((prev: any[]) => prev.filter((r) => r.slug !== slug));
      if (p.articleSlug === slug) {
        p.setLoadedArticle(null);
        p.setArticleSlug("");
        p.setKbMode("library");
      }
      await p.refreshDraftCount();
    } finally {
      p.setDeleteWorking(false);
    }
  }

  async function doPublishDraft(slug: string) {
    p.setPublishWorking(true);
    p.setConfirmPublishSlug(null);
    try {
      let response = await adminPost<{ ok: true; slug: string; chunkQuality?: ChunkQuality }>("/api/kb/admin/publishDraft", { slug });
      if (!response.ok && response.code === "LOW_CHUNK_QUALITY") {
        const quality = (response.detail as { chunkQuality?: ChunkQuality } | undefined)?.chunkQuality;
        if (quality) {
          p.setChunkQuality(quality);
          p.setChunkQualityAdvice(adviseFromChunkQuality(quality));
        }
        if (window.confirm(`Chunk quality is ${quality?.risk ?? "low"} (${quality?.totalChunkChars ?? 0} total chars). Publish anyway?`)) {
          response = await adminPost<{ ok: true; slug: string; chunkQuality?: ChunkQuality }>("/api/kb/admin/publishDraft", { slug, forcePublish: true });
        }
      }
      if (!response.ok) return p.showNote({ tone: "error", title: "Publish failed", text: response.error });
      if (response.data?.chunkQuality) {
        p.setChunkQuality(response.data.chunkQuality);
        p.setChunkQualityAdvice(adviseFromChunkQuality(response.data.chunkQuality));
      }
      p.showNote({ tone: "success", title: "Article published", text: `"${slug}" is now published and live in the knowledge base.` });
      p.setResults((prev: any[]) => prev.map((r) => (r.slug === slug ? { ...r, status: "published" } : r)));
      if (p.loadedArticle?.slug === slug) {
        p.setLoadedArticle((prev: any) => (prev ? { ...prev, status: "published" } : prev));
        p.setArticleStatus("published");
      }
      await p.refreshDraftCount();
    } finally {
      p.setPublishWorking(false);
    }
  }

  async function doPurge(slug: string) {
    p.setDeleteWorking(true);
    p.setConfirmPurgeSlug(null);
    try {
      const response = await adminPost<{ ok: true; slug: string }>("/api/kb/admin/purgeArticle", { slug });
      if (!response.ok) return p.showNote({ tone: "error", title: "Purge failed", text: response.error });
      p.showNote({ tone: "success", title: "Article permanently deleted", text: `"${slug}" has been removed from the database.` });
      p.setTrashResults((prev: TrashResult[]) => prev.filter((r) => r.slug !== slug));
    } finally {
      p.setDeleteWorking(false);
    }
  }

  async function loadTrash() {
    p.setTrashLoading(true);
    try {
      const response = await adminPost<{ ok: true; drafts: any[] }>("/api/kb/admin/drafts", { mode: "trash", limit: 100 });
      if (!response.ok) return p.showNote({ tone: "error", title: "Trash load failed", text: response.error });
      p.setTrashResults((response.data.drafts || []) as TrashResult[]);
    } finally {
      p.setTrashLoading(false);
    }
  }

  return { doSoftDelete, doPublishDraft, doPurge, loadTrash };
}
